/**
 * Temp_SourceViewer — 업로드한 견적서 원본을 화면 안에서 보여 주는 임시 컴포넌트 (D-07 §3.5)
 *
 * 표준에 파일 뷰어가 없어 임시 컴포넌트로 만든다(색·높이 지정 없음 — 배치는 부모가 정한다). 승격·치환은 UI팀이 판단한다.
 * - 이미지(png·jpg): 패널 폭에 맞춰 표시(100%) · 막대(− ＋ 맞춤)와 Ctrl+휠로 확대 · 마우스로 끌어서 이동(Temp_zoom)
 * - PDF: 브라우저 내장 뷰어(iframe) — 쪽 이동·확대는 브라우저가 제공한다
 * - 엑셀(.xlsx): 인쇄 미리보기처럼 그린다(Temp_ExcelPreview). 읽지 못하는 파일은 원본 내려받기로 안내한다
 * - 엑셀(.xls, 구형 바이너리): 화면에서 읽을 수 없어 원본 내려받기만 제공한다
 */
import { useEffect, useState } from 'react'
import { Button, EmptyState, IconGuide } from '@indipia/ui-core'
import { Temp_ExcelPreview } from './Temp_ExcelPreview'
import { stepZoom, Temp_ZoomBar, Temp_ZoomHint, useCtrlWheelZoom, useDragPan, useZoomAnchor } from './Temp_zoom'

/** 이미지 — 배율 1 = 패널 폭에 맞춤(너비 %로 늘린다) */
function ImageView({ url, name }: { url: string; name: string }) {
  const [zoom, setZoom] = useState(1)
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null)
  const step = (dir: 1 | -1) => setZoom(z => stepZoom(z, dir))
  useCtrlWheelZoom(scrollEl, step)
  useDragPan(scrollEl)
  useZoomAnchor(scrollEl, zoom)
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-end border-b border-border px-04 py-02">
        <Temp_ZoomBar scale={zoom} fitted={zoom === 1} onStep={step} onFit={() => setZoom(1)} />
      </div>
      <Temp_ZoomHint />
      <div ref={setScrollEl} className="min-h-0 flex-1 overflow-auto">
        <img src={url} alt={`견적서 원본 ${name}`} className="block max-w-none" style={{ width: `${zoom * 100}%` }} />
      </div>
    </div>
  )
}

type Kind = 'image' | 'pdf' | 'xlsx' | 'xls' | 'other'

const kindOf = (f: File): Kind =>
  /\.(png|jpe?g)$/i.test(f.name) ? 'image'
    : /\.pdf$/i.test(f.name) ? 'pdf'
      : /\.xlsx$/i.test(f.name) ? 'xlsx'
        : /\.xls$/i.test(f.name) ? 'xls'
          : 'other'

export function Temp_SourceViewer({ file }: { file: File }) {
  // 파일을 가리키는 임시 주소 — 파일이 바뀌거나 사라지면 해제한다(StrictMode 에서 두 번 마운트돼도 안전하게 효과 안에서 만든다)
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file])

  const kind = kindOf(file)
  const download = () => {
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
  }

  if (!url) return null
  if (kind === 'image') return <ImageView url={url} name={file.name} />
  if (kind === 'pdf') return <iframe src={url} title={`견적서 원본 ${file.name}`} className="block h-full w-full border-0" />

  const downloadOnly = (title: string) => (
    <EmptyState
      icon={<IconGuide />}
      title={title}
      description="원본 파일을 내려받아 확인해 주세요"
      action={<Button variant="secondary" size="sm" onClick={download}>원본 내려받기</Button>}
    />
  )
  if (kind === 'xlsx') return <Temp_ExcelPreview file={file} fallback={downloadOnly('이 엑셀 파일은 화면에서 열지 못했어요')} />
  return downloadOnly(kind === 'xls' ? '구형 엑셀(.xls)은 화면에서 바로 열 수 없어요' : '이 형식은 화면에서 열 수 없어요')
}
